import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
  useTheme,
  Select,
  MenuItem,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { fetchGet, formatStat, fetchPut, getStringHash } from "../util/helpers";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useContext, useEffect, useMemo, useState } from "react";
import toast, { Toaster } from "react-hot-toast";

import {
  IconApps,
  IconAppsFilled,
  IconBrandFacebook,
  IconBrandInstagram,
  IconBrandLinkedin,
  IconBrandTiktok,
  IconBrandYoutube,
  IconBuilding,
  IconChartArrowsVertical,
  IconChartBar,
  IconChartDonut,
  IconChartLine,
  IconCoinMonero,
  IconCrown,
  IconEdit,
  IconEye,
  IconHeart,
  IconMessage,
  IconShare,
  IconTrendingUp,
  IconFileDescription,
  IconInfoCircle,
  IconLink,
  IconListCheck,
  IconMapPin,
  IconMicrophone2,
  IconPresentation,
  IconSearch,
  IconUserPlus,
  IconUsers,
  IconUsersGroup,
} from "@tabler/icons-react";
import { getRandomNumber } from "../util/helpers";
import ViewsLineChart from "../components/ApexLineChart";
import ReactApexChart from "react-apexcharts";
import UserContext from "../contexts/UserContext";
import NewOutreachTable from "./NewOutreachTable";
import CreatorCarousel from "../components/CreatorCarousel";
import WorkstreamTab from "./WorkstreamTab";
import ApplicantTab from "./ApplicantTab";
import LiveContent from "./LiveContent";
import InviteFromListDialog from "./InviteFromListDialog";
import InsightDrawer from "./InsightDrawer";

const formatCost = (value) => {
  if (value === 0) return "0.00";
  if (Math.abs(value) >= 0.01) return value.toFixed(2);
  if (Math.abs(value) >= 0.001) return value.toFixed(3);
  return value.toFixed(4);
};

const CAMPAIGN_TABS = [
  { name: "Details", value: "details" },
  { name: "Outreach", value: "outreach" },
  { name: "Applicants", value: "applicants" },
  { name: "Workstream", value: "workstream" },
  { name: "Analytics", value: "analytics" },
  { name: "Live Content", value: "live" },
];
const VALID_TAB_VALUES = new Set(CAMPAIGN_TABS.map((t) => t.value));

export default function NewCampaignPage() {
  const [campaign, setCampaign] = useState({});
  const { campaignId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useContext(UserContext);
  const theme = useTheme();
  const [matches, setMatches] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [applications, setApplications] = useState([]);
  const [outreach, setOutreach] = useState([]);
  const [outreachLoading, setOutreachLoading] = useState(false);
  const [analytics, setAnalytics] = useState([]);
  const [conversions, setConversions] = useState([]);
  const [creatorCost, setCreatorCost] = useState(0);
  const [gettingStartedOpen, setGettingStartedOpen] = useState(false);
  const [addNewListOpen, setAddNewListOpen] = useState(false);
  const [outreachNameSearch, setOutreachNameSearch] = useState("");
  const [insightCreator, setInsightCreator] = useState(null)
  const [campaignConversions, setCampaignConversions] = useState([]);
  const [deliverables, setDeliverables] = useState([]);
  const navigate = useNavigate();

  const tabValue = VALID_TAB_VALUES.has(searchParams.get("tab")) ? searchParams.get("tab") : "details";
  const setTabValue = (value) => setSearchParams({ tab: value });

  const fetchCampaign = async () => {
    if (campaignId) {
      console.log("id", campaignId);
      const response = await fetchGet(`/brand/campaigns/${campaignId}`);
      if (response.result) {
        setCampaign(response.result);
      }
    }
  };

  // Creators on our platform that have applied to the campaign
  const fetchApplications = async () => {
    // setIsLoading(true);
    if (campaignId) {
      const response = await fetchGet(
        `/brand/campaigns/${campaignId}/responses`
      );
      if (response.result) {
        const creatorTemp = [...response.result];
        creatorTemp.sort(
          (a, b) =>
            (b.stats ? b.stats.totalFollowers : 0) -
            (a.stats ? a.stats.totalFollowers : 0)
        );
        // if (
        //     creatorTemp.length > 0 &&
        //     creatorTemp[0].stats &&
        //     creatorTemp[0].stats.totalFollowers > 100000
        // ) {
        //     setBestMatchCreators(creatorTemp.slice(0, 6));
        // }
        setApplications(response.result);
      }
      // setIsLoading(false);
    }
  };

  // Creators not on our platform that the brand has outreached to for this campaign.
  // Only fetched when user opens the Outreach tab to avoid slowing initial page load.
  const fetchOutreach = () => {
    if (!campaignId) return;
    setOutreachLoading(true);
    fetchGet(`/brand/outreach?campaignId=${campaignId}`)
      .then((result) => result.result)
      .then((result) => {
        setOutreach(result.map((res) => ({ ...res, id: res.email })));
      })
      .finally(() => setOutreachLoading(false));
  };

  // Creators that have been matched to the campaign. They accepted the invite or the brand accepted their application.
  // Creators must sign up for the platform to be matched.
  // TODO: store creator object in matches
  const fetchMatches = async () => {
    if (campaignId) {
      const response = await fetchGet(`/brand/campaigns/${campaignId}/matches`);
      if (response.result) {
        // Remove duplicates based on userDocId
        const uniqueCreators = [];
        const seenUserDocIds = new Set();
        
        for (const creator of response.result) {
          if (!seenUserDocIds.has(creator.userDocId)) {
            seenUserDocIds.add(creator.userDocId);
            uniqueCreators.push(creator);
            
            try {
              const analyticsResponse = await fetchGet(
                `/brand/campaigns/${campaignId}/creator/${creator.userDocId}/analytics`
              );
              if (analyticsResponse.result) {
                analyticsResponse.result.sort((a, b) => new Date(a.date) - new Date(b.date));
                creator.stats = analyticsResponse.result;
              }
            } catch (error) {
              console.log("error", error);
            }
          }
        }
        setMatches(uniqueCreators);
      }
    }
  };

  const fetchRecommendations = async () => {
    if (campaignId) {
      const response = await fetchGet(
        `/brand/campaigns/${campaignId}/recommendations`
      );
      if (response.result) {
        setRecommendations(response.result);
      }
    }
  };

  useEffect(() => {
    fetchCampaign();
    fetchMatches();
    fetchRecommendations();
    fetchApplications();
  }, [campaignId, user]);

  // Lazy-load outreach only when user opens the Outreach tab
  useEffect(() => {
    if (tabValue === "outreach" && campaignId) {
      fetchOutreach();
    }
  }, [tabValue, campaignId]);

  const fetchAnalytics = async () => {
    const result = await fetchGet(`/brand/campaigns/${campaignId}/analytics`);
    result.result.sort((a, b) => new Date(a.date) - new Date(b.date));
    setAnalytics(result.result);
  };

  const fetchCampaignConversions = async () => {
    const result = await fetchGet(`/brand/campaigns/${campaignId}/creator/_/conversions`);
    setCampaignConversions(result.result || []);
  };

  const fetchDeliverables = async () => {
    const result = await fetchGet(`/brand/deliverables?campaignId=${campaignId}`);
    if (result.result) {
      setDeliverables(result.result.filter(d => d.platform !== "draft" && d.platform !== "concept" && d.platform !== "creator-brief" && d.platform !== "Approve-Content" && d.platform !== "Approve-Concept" && d.platform !== "Creator-Feedback"));
    }
  };

  useEffect(() => {
    if (campaignId) {
      fetchAnalytics();
      fetchCampaignConversions();
      fetchDeliverables();
    }
  }, [campaignId]);

  useEffect(() => {
    if (tabValue === "analytics" && campaignId) {
      fetchDeliverables();
    }
  }, [tabValue]);

  const postDeliverables = useMemo(() => deliverables.filter((d) =>
    d.platform !== "draft" &&
    d.platform !== "concept" &&
    d.platform !== "creator-brief" &&
    d.platform !== "creator-agreement" &&
    d.link
  ), [deliverables]);

  useEffect(() => {
    if (tabValue === "analytics" && postDeliverables.length > 0) {
      console.log("analytics page posts published", postDeliverables.length, postDeliverables);
    }
  }, [tabValue, postDeliverables]);

  const allData = useMemo(() => {
    const total = {};
    const result = [];
    analytics.forEach((data) => {
      const formattedDate = new Date(data.date).toISOString().slice(0, 10);
      if (total[formattedDate] === undefined) {
        total[formattedDate] = {
          views: 0,
          shares: 0,
          likes: 0,
          comments: 0,
          engagement: 0,
        };
      }
      total[formattedDate].views += data.views;
      total[formattedDate].shares += data.shares;
      total[formattedDate].likes += data.likes;
      total[formattedDate].comments += data.comments;
      total[formattedDate].engagement += data.engagement;
    });
    for (let key in total) {
      result.push([total[key], key]);
    }
    result.sort((a, b) => a[0] - b[0]);
    return [
      result.map((r) => r[0]),
      result.map((r) => new Date(r[1]).toISOString().slice(0, 10)),
    ];
  }, [analytics]);
  //   const engagementData = useMemo(() => analytics.map(data => data.engagement), [analytics])
  const allYValues = allData[0];
  const allXValues = allData[1];
  const advYValues = useMemo(() => {
    const newList = [];
    allYValues.forEach((data) => {
      const tempData = { ...data };
      for (const [key, value] of Object.entries(tempData)) {
        tempData[key] =
          creatorCost !== "0" && creatorCost
            ? parseFloat(creatorCost) / value
            : 0;
      }
      tempData["mille"] =
        creatorCost !== "0" && creatorCost
          ? parseFloat(creatorCost) / (parseFloat(data["views"]) / 1000)
          : 0;
      newList.push({ ...tempData });
    });
    return newList;
  }, [allYValues, creatorCost]);

  const convValues = useMemo(() => {
    const allConversions = {};
    const xValues = [];
    const yValues = [];
    conversions.forEach((data) => {
      const productLink = data["id"];
      allConversions[productLink] =
        allConversions[productLink] || 0 + data["visits"];
    });
    for (let key in allConversions) {
      xValues.push(key);
      const tempY =
        creatorCost !== "0" && creatorCost
          ? parseFloat(creatorCost) / parseFloat(allConversions[key])
          : 0;
      yValues.push(tempY);
    }

    return [[...xValues], [...yValues]];
  }, [conversions, creatorCost]);

  const campaignMetrics = useMemo(() => {
    const budgetEngaged = deliverables.reduce((sum, d) => sum + (parseFloat(d.totalPrice) || 0), 0);
    const latestStats = allYValues[allYValues.length - 1];
    const totalViews = latestStats?.views || 0;
    const totalEngagement = latestStats?.engagement || 0;
    const totalClicks = campaignConversions.reduce((sum, c) => sum + (c.visits || 0), 0);

    return {
      budgetEngaged,
      cpv: totalViews > 0 ? budgetEngaged / totalViews : 0,
      cpe: totalEngagement > 0 ? budgetEngaged / totalEngagement : 0,
      cpc: totalClicks > 0 ? budgetEngaged / totalClicks : 0,
      totalClicks,
    };
  }, [deliverables, allYValues, campaignConversions]);

  const getCreatorMetrics = (creator) => {
    const creatorPostDeliverables = postDeliverables.filter(d => d.userDocId === creator.userDocId);
    const creatorDeliverables = deliverables.filter(d => d.userDocId === creator.userDocId);
    const postCount = creatorPostDeliverables.length;
    const totalCost = creatorDeliverables.reduce((sum, d) => sum + (parseFloat(d.totalPrice) || 0), 0);
    const latestStats = creator?.stats?.length > 0 ? creator.stats[creator.stats.length - 1] : null;
    const totalViews = latestStats?.views || 0;
    const totalEngagement = latestStats?.engagement || 0;
    const creatorConversions = campaignConversions.filter(c => c.creatorUserDocId === creator.userDocId);
    const totalClicks = creatorConversions.reduce((sum, c) => sum + (c.visits || 0), 0);

    return {
      postCount,
      totalCost,
      cpv: totalViews > 0 ? totalCost / totalViews : 0,
      cpe: totalEngagement > 0 ? totalCost / totalEngagement : 0,
      cpc: totalClicks > 0 ? totalCost / totalClicks : 0,
      ctr: totalViews > 0 ? (totalClicks / totalViews) * 100 : 0,
    };
  };

  const engagementRateOverTime = useMemo(() => {
    return allYValues.map(d => d.views > 0 ? parseFloat(((d.engagement / d.views) * 100).toFixed(2)) : 0);
  }, [allYValues]);

  const campaignCostOverTime = useMemo(() => {
    const budget = campaignMetrics.budgetEngaged;
    if (budget <= 0) return { cpv: [], cpe: [] };
    return {
      cpv: allYValues.map(d => d.views > 0 ? parseFloat(formatCost(budget / d.views)) : 0),
      cpe: allYValues.map(d => d.engagement > 0 ? parseFloat(formatCost(budget / d.engagement)) : 0),
    };
  }, [allYValues, campaignMetrics.budgetEngaged]);

  const creatorComparisonData = useMemo(() => {
    const creators = matches.filter(c => c.name && c.stats?.length > 0);
    return {
      names: creators.map(c => c.name),
      views: creators.map(c => c.stats[c.stats.length - 1]?.views || 0),
      engagement: creators.map(c => c.stats[c.stats.length - 1]?.engagement || 0),
    };
  }, [matches]);

  const latestTotals = useMemo(() => {
    const latest = allYValues[allYValues.length - 1];
    return {
      views: latest?.views || 0,
      likes: latest?.likes || 0,
      comments: latest?.comments || 0,
      shares: latest?.shares || 0,
      engagement: latest?.engagement || 0,
    };
  }, [allYValues]);

  const engagementComposition = useMemo(() => {
    const { likes, comments, shares } = latestTotals;
    const total = likes + comments + shares;
    if (total === 0) return null;
    return { labels: ["Likes", "Comments", "Shares"], series: [likes, comments, shares] };
  }, [latestTotals]);

  const dailyGrowth = useMemo(() => {
    if (allYValues.length < 2) return null;
    const viewsGrowth = [];
    const engGrowth = [];
    for (let i = 1; i < allYValues.length; i++) {
      const prevViews = allYValues[i - 1].views || 1;
      const prevEng = allYValues[i - 1].engagement || 1;
      viewsGrowth.push(parseFloat((((allYValues[i].views - allYValues[i - 1].views) / prevViews) * 100).toFixed(2)));
      engGrowth.push(parseFloat((((allYValues[i].engagement - allYValues[i - 1].engagement) / prevEng) * 100).toFixed(2)));
    }
    return { viewsGrowth, engGrowth, dates: allXValues.slice(1) };
  }, [allYValues, allXValues]);

  const platformBreakdown = useMemo(() => {
    const platforms = {};
    analytics.forEach(d => {
      const p = (d.platform || "unknown").toString().toLowerCase();
      if (!platforms[p]) platforms[p] = { views: 0, engagement: 0 };
      platforms[p].views += d.views || 0;
      platforms[p].engagement += d.engagement || 0;
    });
    const labels = Object.keys(platforms);
    if (labels.length === 0) return null;
    const formatLabel = (l) => {
      const name = l.charAt(0).toUpperCase() + l.slice(1);
      const icons = { instagram: "Instagram", tiktok: "TikTok", youtube: "YouTube", facebook: "Facebook", linkedin: "LinkedIn" };
      return icons[name.toLowerCase()] || name;
    };
    return {
      labels: labels.map(formatLabel),
      views: labels.map(l => platforms[l].views),
      engagement: labels.map(l => platforms[l].engagement),
    };
  }, [analytics]);
  const creatorCostComparison = useMemo(() => {
    const creators = matches.filter(c => c.name && c.stats?.length > 0);
    const data = creators.map(c => {
      const m = getCreatorMetrics(c);
      return { name: c.name, cpv: m.cpv, cpe: m.cpe, cost: m.totalCost };
    }).filter(d => d.cost > 0);
    if (data.length === 0) return null;
    return {
      names: data.map(d => d.name),
      cpv: data.map(d => parseFloat(formatCost(d.cpv))),
      cpe: data.map(d => parseFloat(formatCost(d.cpe))),
    };
  }, [matches, deliverables, campaignConversions]);

  const topPerformer = useMemo(() => {
    const creators = matches.filter(c => c.name && c.stats?.length > 0);
    if (creators.length === 0) return null;
    return creators.reduce((best, c) => {
      const views = c.stats[c.stats.length - 1]?.views || 0;
      return views > (best.stats[best.stats.length - 1]?.views || 0) ? c : best;
    }, creators[0]);
  }, [matches]);

  const creatorPerformanceHeaders = [
    "Creator",
    "# Posts",
    "Views",
    "Engagement",
    "CPV",
    "CPE",
    "Action",
  ];

  const platformToHeader = {
    instagram: (
      <Stack direction="row" spacing={2} alignItems="center">
        <IconBrandInstagram
          className="icon-colored"
          size="40"
          color="var(--main-text-color)"
        />
        <Typography variant="bodyregm" color="var(--main-text-color)">
          Instagram
        </Typography>
      </Stack>
    ),
    tiktok: (
      <Stack direction="row" spacing={2} alignItems="center">
        <IconBrandTiktok
          className="icon-colored"
          size="40"
          color="var(--main-text-color)"
        />
        <Typography variant="bodyregm" color="var(--main-text-color)">
          TikTok
        </Typography>
      </Stack>
    ),
    youtube: (
      <Stack direction="row" spacing={2} alignItems="center">
        <IconBrandYoutube
          className="icon-colored"
          size="40"
          color="var(--main-text-color)"
        />
        <Typography variant="bodyregm" color="var(--main-text-color)">
          Youtube
        </Typography>
      </Stack>
    ),
    facebook: (
      <Stack direction="row" spacing={2} alignItems="center">
        <IconBrandFacebook
          className="icon-colored"
          size="40"
          color="var(--main-text-color)"
        />
        <Typography variant="bodyregm" color="var(--main-text-color)">
          Facebook
        </Typography>
      </Stack>
    ),
    linkedin: (
      <Stack direction="row" spacing={2} alignItems="center">
        <IconBrandLinkedin
          className="icon-colored"
          size="40"
          color="var(--main-text-color)"
        />
        <Typography variant="bodyregm" color="var(--main-text-color)">
          LinkedIn
        </Typography>
      </Stack>
    ),
  };
  console.log("allData", allData, applications);

  const copyInviteLink = async ()=>{
    try{
      const invitationLink = `${window.location.origin}/match/${campaignId}`; 
      await navigator.clipboard.writeText(invitationLink);
      toast.success("Link copied!")
    }catch(e){
      toast.error("Failed to copy link!");
    }
  }

  const handleAddToLists = () => {
    // setAddNewListOpen(true);
  }

  return (
    <Box sx={{ height: "100%", width: "100%" }}>
            <Toaster position="top-center" toastOptions={{ duration: 3000 }} />
            <Dialog
        open={gettingStartedOpen}
        fullWidth
        onClose={() => setGettingStartedOpen(false)}
        PaperProps={{
          style: { borderRadius: 20, minWidth: "80vw", maxHeight:"80vh" },
        }}
      >
        <DialogTitle>Getting Started on Your Campaign</DialogTitle>

        <DialogContent>
 

          <Typography style={{ fontSize: 16, fontweight: 1000 }}>
            1. Receiving Creator Applications:
          </Typography>
          <Typography style={{ fontSize: 14 }}>
            In order to receive applications for your campaign you must click
            the "Make Public" Button at the top right corner of your campaigns
            page. Don't worry this is campaign is internal to Gigl. Make your
            campaign hidden if you are still in the planning phase and editing
            you campaign.
          </Typography>
          <br />
          <br />

          <Typography style={{ fontSize: 16, fontweight: 1000 }}>
            2. Identifying New Creators:
          </Typography>
          <Typography style={{ fontSize: 14 }}>
            Identify new creators for your campaign in the Discovery Section
            below. Easily filter to find your perfect match, and directly
            outreach to invite new creators to apply to work with you.
          </Typography>

          <br />
          <br />
          <Typography style={{ fontSize: 16, fontweight: 1000 }}>
            3. Working with Creators:
          </Typography>
          <Typography style={{ fontSize: 14 }}>
            Once you confirm your partnership with a creator, you will unlock
            your creator workstream section, where you can easily collaborate
            with each creator for your campaign.
          </Typography>

          <br />
          <br />
        
          <Typography  style={{ fontSize: 16, fontweight: 1000 }}>
            4. Tips to get more creators interested in working with you:
          </Typography>
          <br />
          <Typography style={{ fontSize: 14 }}>
            a. Keep your campaign description short and sweet (1-3 sentences):
            Example: Our Brand is [X], we are looking for [Z] type of creators
            to join our (paid,affiliate,merch) campaign, and we want to get
            viewers excited for this new launch.
            <br />
            <br />
            b. Don't include any links to documents/briefs/contracts at this
            point. Share after you have confirmed the partnership.
            <br />
            <br />
            c. Have clear deliverables. Example: We are looking for 1 IG video,
            1 static post, and 2 stories.
            <br />
            <br />
            d. Its better to have clear compensation. This will bring relevant
            creators to your campaign
            <br />
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => navigate("/campaigns/FAQ")}>Visit FAQ</Button>
          <Button
            onClick={() => {
              setGettingStartedOpen(false);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        sx={{
          borderBottom: "1px solid var(--neutral-200)",
          backgroundColor: "white",
          paddingTop: "24px",
          paddingX: "28px",
          paddingBottom: 0,
        }}
      >
        <Stack direction="column" justifyContent="space-between" height="100%">
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <Stack direction="row" spacing={2.5} alignItems="center">
              <Box
                sx={{
                  position: "relative",
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #9F25F6 0%, #5CA4FB 100%)",
                  padding: "2.5px",
                  flexShrink: 0,
                }}
              >
                <img
                  style={{
                    width: "100%",
                    height: "100%",
                    borderRadius: "50%",
                    objectFit: "cover",
                    display: "block",
                    border: "2.5px solid white",
                  }}
                  src={campaign.image}
                  alt={campaign.name}
                />
              </Box>
              <Stack direction="column" spacing={0.75}>
                <Typography variant="h3" color="var(--main-text-color)" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                  {campaign.name}
                </Typography>
                <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
                  <Stack direction="row" gap={0.75} alignItems="center">
                    <Select
                      value={campaign.active ? "active" : "inactive"}
                      onChange={async (e) => {
                        const newActiveValue = e.target.value === "active";
                        setCampaign({ ...campaign, active: newActiveValue });
                        try {
                          await fetchPut(`/brand/campaigns/${campaignId}`, {
                            ...campaign,
                            active: newActiveValue,
                          });
                        } catch (error) {
                          console.error("Error updating campaign status:", error);
                          setCampaign({ ...campaign, active: !newActiveValue });
                        }
                      }}
                      renderValue={(value) => (
                        <Stack direction="row" alignItems="center" gap={1} sx={{ py: 0.25 }}>
                          <Box
                            sx={{
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              backgroundColor: value === "active" ? "#16a34a" : "#dc2626",
                              flexShrink: 0,
                            }}
                          />
                          <span>{value === "active" ? "Active" : "Inactive"}</span>
                        </Stack>
                      )}
                      size="small"
                      displayEmpty
                      sx={{
                        backgroundColor: campaign.active ? "rgba(34, 197, 94, 0.12)" : "rgba(239, 68, 68, 0.12)",
                        color: campaign.active ? "#15803d" : "#b91c1c",
                        borderRadius: "100px",
                        fontSize: "13px",
                        fontWeight: 600,
                        height: "28px",
                        minWidth: 96,
                        "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                        "& .MuiSelect-select": {
                          padding: "0 32px 0 12px !important",
                          display: "flex",
                          alignItems: "center",
                        },
                        "& .MuiSelect-icon": {
                          color: "inherit",
                          right: 10,
                          fontSize: 18,
                          opacity: 0.8,
                        },
                        transition: "background-color 0.2s ease, box-shadow 0.2s ease",
                        "&:hover": {
                          backgroundColor: campaign.active ? "rgba(34, 197, 94, 0.2)" : "rgba(239, 68, 68, 0.2)",
                        },
                        "&.Mui-focused": {
                          boxShadow: campaign.active ? "0 0 0 2px rgba(34, 197, 94, 0.25)" : "0 0 0 2px rgba(239, 68, 68, 0.25)",
                        },
                      }}
                      MenuProps={{
                        disableScrollLock: true,
                        PaperProps: {
                          elevation: 8,
                          sx: {
                            mt: 1.25,
                            minWidth: 120,
                            borderRadius: 2,
                            border: "1px solid var(--neutral-200, #e2e8f0)",
                            boxShadow: "0 10px 40px -10px rgba(0,0,0,0.12), 0 4px 12px -4px rgba(0,0,0,0.08)",
                            "& .MuiList-root": { py: 0.5 },
                          },
                        },
                        MenuListProps: { sx: { py: 0 } },
                      }}
                    >
                      <MenuItem value="active" sx={{ fontSize: "13px", fontWeight: 500, py: 1.25, "&:hover": { backgroundColor: "rgba(34, 197, 94, 0.08)" } }}>
                        <Stack direction="row" alignItems="center" gap={1.25} sx={{ width: "100%" }}>
                          <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "#16a34a" }} />
                          Active
                        </Stack>
                      </MenuItem>
                      <MenuItem value="inactive" sx={{ fontSize: "13px", fontWeight: 500, py: 1.25, "&:hover": { backgroundColor: "rgba(239, 68, 68, 0.08)" } }}>
                        <Stack direction="row" alignItems="center" gap={1.25} sx={{ width: "100%" }}>
                          <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "#dc2626" }} />
                          Inactive
                        </Stack>
                      </MenuItem>
                    </Select>
                    <Typography
                      variant="bodysm"
                      sx={{ color: "var(--body-text-color-200)", fontSize: "12px" }}
                    >
                      {campaign.active ? "Visible" : "Hidden"}
                    </Typography>
                  </Stack>

                  <Box sx={{ width: "1px", height: "14px", backgroundColor: "var(--neutral-200)" }} />

                  <Stack direction="row" gap={0.75} alignItems="center">
                    <Select
                      value={campaign.public ? "public" : "private"}
                      onChange={async (e) => {
                        const newPublicValue = e.target.value === "public";
                        setCampaign({ ...campaign, public: newPublicValue });
                        try {
                          await fetchPut(`/brand/campaigns/${campaignId}`, {
                            ...campaign,
                            public: newPublicValue,
                          });
                        } catch (error) {
                          console.error("Error updating campaign visibility:", error);
                          setCampaign({ ...campaign, public: !newPublicValue });
                        }
                      }}
                      renderValue={(value) => (
                        <Stack direction="row" alignItems="center" gap={1} sx={{ py: 0.25 }}>
                          <Box
                            sx={{
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              backgroundColor: value === "public" ? "#2563eb" : "#7c3aed",
                              flexShrink: 0,
                            }}
                          />
                          <span>{value === "public" ? "Public" : "Private"}</span>
                        </Stack>
                      )}
                      size="small"
                      displayEmpty
                      sx={{
                        backgroundColor: campaign.public ? "rgba(59, 130, 246, 0.12)" : "rgba(139, 92, 246, 0.12)",
                        color: campaign.public ? "#1d4ed8" : "#6d28d9",
                        borderRadius: "100px",
                        fontSize: "13px",
                        fontWeight: 600,
                        height: "28px",
                        minWidth: 96,
                        "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                        "& .MuiSelect-select": {
                          padding: "0 32px 0 12px !important",
                          display: "flex",
                          alignItems: "center",
                        },
                        "& .MuiSelect-icon": {
                          color: "inherit",
                          right: 10,
                          fontSize: 18,
                          opacity: 0.8,
                        },
                        transition: "background-color 0.2s ease, box-shadow 0.2s ease",
                        "&:hover": {
                          backgroundColor: campaign.public ? "rgba(59, 130, 246, 0.2)" : "rgba(139, 92, 246, 0.2)",
                        },
                        "&.Mui-focused": {
                          boxShadow: campaign.public ? "0 0 0 2px rgba(59, 130, 246, 0.25)" : "0 0 0 2px rgba(139, 92, 246, 0.25)",
                        },
                      }}
                      MenuProps={{
                        disableScrollLock: true,
                        PaperProps: {
                          elevation: 8,
                          sx: {
                            mt: 1.25,
                            minWidth: 120,
                            borderRadius: 2,
                            border: "1px solid var(--neutral-200, #e2e8f0)",
                            boxShadow: "0 10px 40px -10px rgba(0,0,0,0.12), 0 4px 12px -4px rgba(0,0,0,0.08)",
                            "& .MuiList-root": { py: 0.5 },
                          },
                        },
                        MenuListProps: { sx: { py: 0 } },
                      }}
                    >
                      <MenuItem value="public" sx={{ fontSize: "13px", fontWeight: 500, py: 1.25, "&:hover": { backgroundColor: "rgba(59, 130, 246, 0.08)" } }}>
                        <Stack direction="row" alignItems="center" gap={1.25} sx={{ width: "100%" }}>
                          <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "#2563eb" }} />
                          Public
                        </Stack>
                      </MenuItem>
                      <MenuItem value="private" sx={{ fontSize: "13px", fontWeight: 500, py: 1.25, "&:hover": { backgroundColor: "rgba(139, 92, 246, 0.08)" } }}>
                        <Stack direction="row" alignItems="center" gap={1.25} sx={{ width: "100%" }}>
                          <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "#7c3aed" }} />
                          Private
                        </Stack>
                      </MenuItem>
                    </Select>
                    <Typography
                      variant="bodysm"
                      sx={{ color: "var(--body-text-color-200)", fontSize: "12px" }}
                    >
                      {campaign.public ? "Open" : "Invite only"}
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Tooltip title={<Typography variant="body2">Invite any creator to join your campaign by sending them your campaign invite link.</Typography>}>
                <Button
                  onClick={copyInviteLink}
                  variant="contained"
                  className="gradient-button"
                  startIcon={<IconUserPlus className="icon" size="18" />}
                  sx={{ textTransform: "none", borderRadius: "10px", fontWeight: 600, px: 2.5 }}
                >
                  Invite Creators
                </Button>
              </Tooltip>
              <Tooltip title="Edit campaign">
                <IconButton
                  aria-label="edit campaign"
                  sx={{
                    border: "1px solid var(--neutral-200)",
                    borderRadius: "10px",
                    width: 40,
                    height: 40,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: "var(--neutral-50)",
                      borderColor: "var(--body-text-color-200)",
                    },
                  }}
                  onClick={() => navigate(`/campaigns/${campaignId}/edit`)}
                >
                  <IconEdit
                    className="icon-colored"
                    size={18}
                    color="var(--body-text-color-600)"
                  />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
          <Tabs
            value={tabValue}
            onChange={(e, value) => setTabValue(value)}
            indicatorColor="secondary"
          >
            {CAMPAIGN_TABS.map((tab) => (
              <Tab
                className={tabValue === tab.value ? "gradient-text" : ""}
                label={tab.name}
                value={tab.value}
                onMouseEnter={tab.value === "outreach" ? () => { if (campaignId && !outreach.length && !outreachLoading) fetchOutreach(); } : undefined}
              />
            ))}
          </Tabs>
        </Stack>
      </Box>
      {tabValue === "analytics" && (
        <Stack direction="column" spacing={3} sx={{ padding: "3%" }}>
          {console.log("analytics tab partners", matches.length, matches)}
          <InsightDrawer
            open={insightCreator !== null}
            onClose={() => setInsightCreator(null)}
            creator={insightCreator}
            deliverables={deliverables.filter(d => d.userDocId === insightCreator?.userDocId)}
            conversions={campaignConversions.filter(c => c.creatorUserDocId === insightCreator?.userDocId)}
          />
          <Typography variant="h3" color={"var(--main-text-color)"}>
            Analytics
          </Typography>
          {/* Section: Performance */}
          <Stack direction="column" spacing={1.5}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: "8px", background: "rgba(59, 130, 246, 0.1)" }}>
                <IconEye size={16} color="#2563eb" />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 600, color: "var(--main-text-color)" }}>Performance</Typography>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Box className="box" sx={{ flex: "1 1 0", minWidth: "120px" }}>
                <Stack direction="column" spacing={1} height="100%" justifyContent="space-between">
                  <Typography variant="bodyregm" color="var(--body-text-color-600)">Total Reach</Typography>
                  <Typography variant="h2">{formatStat(latestTotals.views)}</Typography>
                </Stack>
              </Box>
              <Box className="box" sx={{ flex: "1 1 0", minWidth: "120px" }}>
                <Stack direction="column" spacing={1} height="100%" justifyContent="space-between">
                  <Typography variant="bodyregm" color="var(--body-text-color-600)">Total Engagement</Typography>
                  <Typography variant="h2">{formatStat(latestTotals.engagement)}</Typography>
                </Stack>
              </Box>
              <Box className="box" sx={{ flex: "1 1 0", minWidth: "120px" }}>
                <Stack direction="column" spacing={1} height="100%" justifyContent="space-between">
                  <Typography variant="bodyregm" color="var(--body-text-color-600)">Likes</Typography>
                  <Typography variant="h2">{formatStat(latestTotals.likes)}</Typography>
                </Stack>
              </Box>
              <Box className="box" sx={{ flex: "1 1 0", minWidth: "120px" }}>
                <Stack direction="column" spacing={1} height="100%" justifyContent="space-between">
                  <Typography variant="bodyregm" color="var(--body-text-color-600)">Comments</Typography>
                  <Typography variant="h2">{formatStat(latestTotals.comments)}</Typography>
                </Stack>
              </Box>
              <Box className="box" sx={{ flex: "1 1 0", minWidth: "120px" }}>
                <Stack direction="column" spacing={1} height="100%" justifyContent="space-between">
                  <Typography variant="bodyregm" color="var(--body-text-color-600)">Shares</Typography>
                  <Typography variant="h2">{formatStat(latestTotals.shares)}</Typography>
                </Stack>
              </Box>
            </Stack>
          </Stack>

          <Divider sx={{ my: 1 }} />

          {/* Section: Campaign Activity */}
          <Stack direction="column" spacing={1.5}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: "8px", background: "rgba(139, 92, 246, 0.1)" }}>
                <IconChartBar size={16} color="#7c3aed" />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 600, color: "var(--main-text-color)" }}>Campaign Activity</Typography>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Box className="box" sx={{ flex: "1 1 0", minWidth: "120px" }}>
                <Stack direction="column" spacing={1} height="100%" justifyContent="space-between">
                  <Typography variant="bodyregm" color="var(--body-text-color-600)">Engagement Rate</Typography>
                  <Typography variant="h2">
                    {latestTotals.views > 0 ? ((latestTotals.engagement / latestTotals.views) * 100).toFixed(2) : "0.00"}%
                  </Typography>
                </Stack>
              </Box>
              <Box className="box" sx={{ flex: "1 1 0", minWidth: "120px" }}>
                <Stack direction="column" spacing={1} height="100%" justifyContent="space-between">
                  <Typography variant="bodyregm" color="var(--body-text-color-600)">Partners</Typography>
                  <Typography variant="h2">{matches.length}</Typography>
                </Stack>
              </Box>
              <Box className="box" sx={{ flex: "1 1 0", minWidth: "120px" }}>
                <Stack direction="column" spacing={1} height="100%" justifyContent="space-between">
                  <Typography variant="bodyregm" color="var(--body-text-color-600)">Total Posts</Typography>
                  <Typography variant="h2">{postDeliverables.length}</Typography>
                </Stack>
              </Box>
              <Box className="box" sx={{ flex: "1 1 0", minWidth: "120px" }}>
                <Stack direction="column" spacing={1} height="100%" justifyContent="space-between">
                  <Typography variant="bodyregm" color="var(--body-text-color-600)">Conversions</Typography>
                  <Typography variant="h2">{formatStat(campaignMetrics.totalClicks)}</Typography>
                </Stack>
              </Box>
              <Box className="box" sx={{ flex: "1 1 0", minWidth: "120px" }}>
                <Stack direction="column" spacing={1} height="100%" justifyContent="space-between">
                  <Typography variant="bodyregm" color="var(--body-text-color-600)">Avg Views/Creator</Typography>
                  <Typography variant="h2">{matches.filter(c => c.stats?.length > 0).length > 0 ? formatStat(Math.round(latestTotals.views / matches.filter(c => c.stats?.length > 0).length)) : "0"}</Typography>
                </Stack>
              </Box>
            </Stack>
          </Stack>

          <Divider sx={{ my: 1 }} />

          {/* Section: Budget & Cost */}
          <Stack direction="column" spacing={1.5}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: "8px", background: "rgba(34, 197, 94, 0.1)" }}>
                <IconCoinMonero size={16} color="#16a34a" />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 600, color: "var(--main-text-color)" }}>Budget & Cost</Typography>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Box className="box" sx={{ flex: "1 1 0", minWidth: "140px" }}>
                <Stack direction="column" spacing={1} height="100%" justifyContent="space-between">
                  <Typography variant="bodyregm" color="var(--body-text-color-600)">Budget Engaged</Typography>
                  <Typography variant="h2">${campaignMetrics.budgetEngaged.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                </Stack>
              </Box>
              <Box className="box" sx={{ flex: "1 1 0", minWidth: "140px" }}>
                <Stack direction="column" spacing={1} height="100%" justifyContent="space-between">
                  <Typography variant="bodyregm" color="var(--body-text-color-600)">CPV</Typography>
                  <Typography variant="h2">${formatCost(campaignMetrics.cpv)}</Typography>
                </Stack>
              </Box>
              <Box className="box" sx={{ flex: "1 1 0", minWidth: "140px" }}>
                <Stack direction="column" spacing={1} height="100%" justifyContent="space-between">
                  <Typography variant="bodyregm" color="var(--body-text-color-600)">CPE</Typography>
                  <Typography variant="h2">${formatCost(campaignMetrics.cpe)}</Typography>
                </Stack>
              </Box>
              <Box className="box" sx={{ flex: "1 1 0", minWidth: "140px" }}>
                <Stack direction="column" spacing={1} height="100%" justifyContent="space-between">
                  <Typography variant="bodyregm" color="var(--body-text-color-600)">CPC</Typography>
                  <Typography variant="h2">${formatCost(campaignMetrics.cpc)}</Typography>
                </Stack>
              </Box>
            </Stack>
          </Stack>

          {/* Top Performer highlight */}
          {topPerformer && (
            <Box className="box" sx={{ background: "linear-gradient(135deg, rgba(124,58,237,0.05) 0%, rgba(59,130,246,0.05) 100%)" }}>
              <Stack direction="row" alignItems="center" spacing={3}>
                <Box className="gradient-circle" sx={{ padding: "8px" }}>
                  <IconCrown size="20" className="icon" />
                </Box>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
                  <img className="profile-image" src={topPerformer?.images?.length > 0 ? topPerformer.images[0] : `../gradient${getStringHash(topPerformer?.name, 10)}.png`} alt={topPerformer.name} />
                  <Stack direction="column" spacing={0}>
                    <Typography variant="bodysm" color="var(--body-text-color-600)">Top Performer</Typography>
                    <Typography variant="h4" color="var(--main-text-color)">{topPerformer.name}</Typography>
                  </Stack>
                </Stack>
                <Stack direction="row" spacing={4}>
                  <Stack direction="column" alignItems="center">
                    <Typography variant="bodysm" color="var(--body-text-color-600)">Views</Typography>
                    <Typography variant="h4" color="var(--main-text-color)">{formatStat(topPerformer.stats[topPerformer.stats.length - 1]?.views)}</Typography>
                  </Stack>
                  <Stack direction="column" alignItems="center">
                    <Typography variant="bodysm" color="var(--body-text-color-600)">Engagement</Typography>
                    <Typography variant="h4" color="var(--main-text-color)">{formatStat(topPerformer.stats[topPerformer.stats.length - 1]?.engagement)}</Typography>
                  </Stack>
                  <Stack direction="column" alignItems="center">
                    <Typography variant="bodysm" color="var(--body-text-color-600)">Eng. Rate</Typography>
                    <Typography variant="h4" color="var(--main-text-color)">
                      {topPerformer.stats[topPerformer.stats.length - 1]?.views > 0
                        ? ((topPerformer.stats[topPerformer.stats.length - 1]?.engagement / topPerformer.stats[topPerformer.stats.length - 1]?.views) * 100).toFixed(2) : "0"}%
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
            </Box>
          )}
          <Stack className="box" direction="column" spacing={2}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  className="gradient-circle"
                  sx={{
                    padding: "8px",
                    alignItems: "center",
                    justifyItems: "center",
                  }}
                >
                  <IconUsers size="20" className="icon" />
                </Box>
                <Typography variant="h4" color={"var(--main-text-color)"}>
                  Creator Performance
                </Typography>
              </Stack>
            </Stack>
            <TableContainer>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    {creatorPerformanceHeaders.map((header) => (
                      <TableCell>
                        <Typography
                          variant="bodysm"
                          color="var(--body-text-color-600)"
                        >
                          {header}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {matches &&
                    matches
                      .filter((creator) => creator.name)
                      .map((creator) => {
                        const application = applications.find(
                          (app) => app.userDocId === creator.userDocId
                        );
                        const metrics = getCreatorMetrics(creator);
                        return (
                          <TableRow key={creator.userDocId}>
                            <TableCell align="left" width="25%">
                              <Stack direction="row" spacing={2}>
                                <img
                                  className="profile-image"
                                  src={
                                    creator?.images?.length > 0
                                      ? creator?.images[0]
                                      : `../gradient${getStringHash(creator?.name, 10)}.png`
                                  }
                                  alt={creator.name}
                                />
                                <Stack direction="column" spacing={0}>
                                  <Typography variant="bodyregm" color="var(--main-text-color)">
                                    {creator.name}
                                  </Typography>
                                  <Typography variant="bodysm" color="var(--body-text-color-600)">
                                    {application?.stats?.channels?.length > 0
                                      ? application?.stats?.channels[0]?.url
                                      : "-"}
                                  </Typography>
                                </Stack>
                              </Stack>
                            </TableCell>
                            <TableCell align="left" width="8%">
                              <Typography variant="bodysm" color="var(--main-text-color)">
                                {metrics.postCount || "-"}
                              </Typography>
                            </TableCell>
                            <TableCell align="left" width="12%">
                              <Typography variant="bodysm" color="var(--main-text-color)">
                                {creator?.stats?.length === 0 ? "-" : formatStat(creator?.stats[creator?.stats?.length - 1]?.views)}
                              </Typography>
                            </TableCell>
                            <TableCell align="left" width="12%">
                              <Typography variant="bodysm" color="var(--main-text-color)">
                                {creator?.stats?.length === 0 ? "-" : formatStat(creator?.stats[creator?.stats?.length - 1]?.engagement)}
                              </Typography>
                            </TableCell>
                            <TableCell align="left" width="10%">
                              <Typography variant="bodysm" color="var(--main-text-color)">
                                {metrics.totalCost > 0 ? `$${formatCost(metrics.cpv)}` : "-"}
                              </Typography>
                            </TableCell>
                            <TableCell align="left" width="10%">
                              <Typography variant="bodysm" color="var(--main-text-color)">
                                {metrics.totalCost > 0 ? `$${formatCost(metrics.cpe)}` : "-"}
                              </Typography>
                            </TableCell>
                            <TableCell align="left" width="12%">
                              <Button
                                variant="outlined"
                                className="outlined-button"
                                color="info"
                                onClick={() => setInsightCreator(creator)}
                              >
                                View Insights
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
          
          {/* Views & Engagement combined */}
          {allXValues.length > 0 && (
            <Stack direction="column" spacing={2} className="box">
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box className="gradient-circle" sx={{ padding: "8px", alignItems: "center", justifyItems: "center" }}>
                  <IconEye size="20" className="icon" />
                </Box>
                <Stack direction="column">
                  <Typography variant="h4" color={"var(--main-text-color)"}>Views & Engagement Over Time</Typography>
                  <Typography variant="bodysm" color="var(--body-text-color-600)">Dual-axis comparison of reach versus engagement</Typography>
                </Stack>
              </Stack>
              <ReactApexChart
                type="line"
                height={380}
                series={[
                  { name: "Views", type: "area", data: allYValues.map(d => d.views) },
                  { name: "Engagement", type: "line", data: allYValues.map(d => d.engagement) },
                ]}
                options={{
                  chart: { type: "line", stacked: false, zoom: { enabled: false }, toolbar: { show: true, tools: { download: true, selection: false, zoom: false, zoomin: false, zoomout: false, pan: false, reset: false } }, fontFamily: "inherit", animations: { enabled: true, easing: "easeinout", speed: 800, animateGradually: { enabled: true, delay: 150 } } },
                  colors: ["#7c3aed", "#f59e0b"],
                  dataLabels: { enabled: false },
                  stroke: { curve: "smooth", width: [2, 3] },
                  fill: { type: ["gradient", "solid"], gradient: { shadeIntensity: 1, opacityFrom: 0.3, opacityTo: 0.02, stops: [0, 90, 100] } },
                  labels: allXValues,
                  xaxis: { type: "datetime", axisBorder: { show: false }, axisTicks: { show: false }, labels: { style: { colors: "#94a3b8", fontSize: "12px", fontWeight: 500 } } },
                  yaxis: [
                    { title: { text: "Views", style: { color: "#7c3aed", fontSize: "12px", fontWeight: 600 } }, labels: { style: { colors: "#94a3b8", fontSize: "11px" }, formatter: (val) => formatStat(val) } },
                    { opposite: true, title: { text: "Engagement", style: { color: "#f59e0b", fontSize: "12px", fontWeight: 600 } }, labels: { style: { colors: "#94a3b8", fontSize: "11px" }, formatter: (val) => formatStat(val) } },
                  ],
                  grid: { borderColor: "#f1f5f9", strokeDashArray: 4, padding: { left: 10, right: 10 } },
                  markers: { size: 0, hover: { size: 5, sizeOffset: 3 } },
                  tooltip: { shared: true, intersect: false, theme: "light", style: { fontSize: "13px" }, x: { format: "MMM dd, yyyy" }, y: { formatter: (val) => val?.toLocaleString() } },
                  legend: { position: "top", horizontalAlign: "left", fontSize: "13px", fontWeight: 600, markers: { radius: 12 }, itemMargin: { horizontal: 12 } },
                }}
              />
            </Stack>
          )}

          {/* Breakdown by social media platform */}
          {platformBreakdown && (
            <Stack direction="column" spacing={2} className="box">
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box className="gradient-circle" sx={{ padding: "8px", alignItems: "center", justifyItems: "center" }}>
                  <IconApps size={20} className="icon" />
                </Box>
                <Stack direction="column">
                  <Typography variant="h4" color={"var(--main-text-color)"}>Breakdown by social media platform</Typography>
                  <Typography variant="bodysm" color="var(--body-text-color-600)">Views and engagement by platform</Typography>
                </Stack>
              </Stack>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <ReactApexChart
                    type="bar"
                    height={320}
                    series={[
                      { name: "Views", data: platformBreakdown.views },
                      { name: "Engagement", data: platformBreakdown.engagement },
                    ]}
                    options={{
                      chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit", animations: { enabled: true, easing: "easeinout", speed: 600 } },
                      colors: ["#7c3aed", "#3b82f6"],
                      plotOptions: { bar: { borderRadius: 6, columnWidth: "55%", borderRadiusApplication: "end" } },
                      dataLabels: { enabled: true, formatter: (val) => formatStat(val), style: { fontSize: "11px", fontWeight: 600, colors: ["#64748b"] }, offsetY: -20 },
                      xaxis: { categories: platformBreakdown.labels, axisBorder: { show: false }, axisTicks: { show: false }, labels: { style: { fontSize: "13px", fontWeight: 600, colors: "#334155" } } },
                      yaxis: { labels: { style: { colors: "#94a3b8", fontSize: "11px" }, formatter: (val) => formatStat(val) } },
                      grid: { borderColor: "#f1f5f9", strokeDashArray: 4 },
                      tooltip: { shared: true, intersect: false, theme: "light", style: { fontSize: "13px" }, y: { formatter: (val) => val?.toLocaleString() } },
                      legend: { position: "top", horizontalAlign: "left", fontSize: "12px", fontWeight: 600, markers: { radius: 12 }, itemMargin: { horizontal: 8 } },
                    }}
                  />
                </Box>
                <Box sx={{ flex: "0 0 280px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <ReactApexChart
                    type="donut"
                    height={280}
                    series={platformBreakdown.views}
                    options={{
                      chart: { type: "donut", fontFamily: "inherit", animations: { enabled: true, easing: "easeinout", speed: 800 } },
                      colors: ["#7c3aed", "#3b82f6", "#10b981", "#f59e0b", "#ef4444"],
                      labels: platformBreakdown.labels,
                      plotOptions: { pie: { donut: { size: "65%", labels: { show: true, name: { fontSize: "12px", fontWeight: 600, color: "#334155" }, value: { fontSize: "16px", fontWeight: 700, color: "#1e293b", formatter: (val) => formatStat(parseInt(val || 0)) }, total: { show: true, label: "Total views", fontSize: "12px", color: "#94a3b8", formatter: (w) => formatStat(w.globals.seriesTotals.reduce((a, b) => a + b, 0)) } } } } },
                      dataLabels: { enabled: false },
                      legend: { position: "bottom", fontSize: "12px", fontWeight: 500, markers: { radius: 10 }, itemMargin: { horizontal: 6, vertical: 2 } },
                      stroke: { width: 0 },
                      tooltip: { y: { formatter: (val) => formatStat(val) } },
                    }}
                  />
                </Box>
              </Stack>
            </Stack>
          )}

          {/* Engagement Breakdown + Donut side by side */}
          {allXValues.length > 0 && (
            <Stack direction="row" spacing={2}>
              <Stack direction="column" spacing={2} className="box" sx={{ flex: 2 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box className="gradient-circle" sx={{ padding: "8px", alignItems: "center", justifyItems: "center" }}>
                    <IconHeart size="20" className="icon" />
                  </Box>
                  <Stack direction="column">
                    <Typography variant="h4" color={"var(--main-text-color)"}>Engagement Breakdown</Typography>
                    <Typography variant="bodysm" color="var(--body-text-color-600)">Likes, comments, and shares over time</Typography>
                  </Stack>
                </Stack>
                <ReactApexChart
                  type="area"
                  height={350}
                  series={[
                    { name: "Likes", data: allYValues.map(d => d.likes) },
                    { name: "Comments", data: allYValues.map(d => d.comments) },
                    { name: "Shares", data: allYValues.map(d => d.shares) },
                  ]}
                  options={{
                    chart: { type: "area", stacked: true, zoom: { enabled: false }, toolbar: { show: false }, fontFamily: "inherit", animations: { enabled: true, easing: "easeinout", speed: 800 } },
                    colors: ["#7c3aed", "#3b82f6", "#10b981"],
                    dataLabels: { enabled: false },
                    stroke: { curve: "smooth", width: 2 },
                    labels: allXValues,
                    xaxis: { type: "datetime", axisBorder: { show: false }, axisTicks: { show: false }, labels: { style: { colors: "#94a3b8", fontSize: "12px", fontWeight: 500 } } },
                    yaxis: { labels: { style: { colors: "#94a3b8", fontSize: "12px", fontWeight: 500 }, formatter: (val) => formatStat(val) } },
                    grid: { borderColor: "#f1f5f9", strokeDashArray: 4, padding: { left: 10, right: 10 } },
                    tooltip: { shared: true, intersect: false, theme: "light", style: { fontSize: "13px" }, x: { format: "MMM dd, yyyy" }, y: { formatter: (val) => val?.toLocaleString() } },
                    legend: { position: "top", horizontalAlign: "left", fontSize: "13px", fontWeight: 600, markers: { radius: 12 }, itemMargin: { horizontal: 12 } },
                    fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.5, opacityTo: 0.05, stops: [0, 90, 100] } },
                    markers: { size: 0, hover: { size: 5, sizeOffset: 3 } },
                  }}
                />
              </Stack>
              {engagementComposition && (
                <Stack direction="column" spacing={2} className="box" sx={{ flex: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Box className="gradient-circle" sx={{ padding: "8px", alignItems: "center", justifyItems: "center" }}>
                      <IconChartDonut size="20" className="icon" />
                    </Box>
                    <Stack direction="column">
                      <Typography variant="h4" color={"var(--main-text-color)"}>Composition</Typography>
                      <Typography variant="bodysm" color="var(--body-text-color-600)">Engagement type split</Typography>
                    </Stack>
                  </Stack>
                  <ReactApexChart
                    type="donut"
                    height={310}
                    series={engagementComposition.series}
                    options={{
                      chart: { type: "donut", fontFamily: "inherit", animations: { enabled: true, easing: "easeinout", speed: 800 } },
                      colors: ["#7c3aed", "#3b82f6", "#10b981"],
                      labels: engagementComposition.labels,
                      plotOptions: { pie: { donut: { size: "70%", labels: { show: true, name: { fontSize: "14px", fontWeight: 600, color: "#334155" }, value: { fontSize: "20px", fontWeight: 700, color: "#1e293b", formatter: (val) => parseInt(val).toLocaleString() }, total: { show: true, label: "Total", fontSize: "13px", color: "#94a3b8", formatter: (w) => formatStat(w.globals.seriesTotals.reduce((a, b) => a + b, 0)) } } } } },
                      dataLabels: { enabled: false },
                      legend: { position: "bottom", fontSize: "13px", fontWeight: 500, markers: { radius: 12 }, itemMargin: { horizontal: 8, vertical: 4 } },
                      stroke: { width: 0 },
                      tooltip: { y: { formatter: (val) => val?.toLocaleString() } },
                    }}
                  />
                </Stack>
              )}
            </Stack>
          )}

          {/* Engagement Rate + Daily Growth side by side */}
          <Stack direction="row" spacing={2}>
            {allXValues.length > 0 && (
              <Stack direction="column" spacing={2} className="box" sx={{ flex: 1 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box className="gradient-circle" sx={{ padding: "8px", alignItems: "center", justifyItems: "center" }}>
                    <IconChartLine size="20" className="icon" />
                  </Box>
                  <Stack direction="column">
                    <Typography variant="h4" color={"var(--main-text-color)"}>Engagement Rate Over Time</Typography>
                    <Typography variant="bodysm" color="var(--body-text-color-600)">% of viewers who engaged</Typography>
                  </Stack>
                </Stack>
                <ReactApexChart
                  type="area"
                  height={300}
                  series={[{ name: "Engagement Rate", data: engagementRateOverTime }]}
                  options={{
                    chart: { type: "area", zoom: { enabled: false }, toolbar: { show: false }, fontFamily: "inherit", animations: { enabled: true, easing: "easeinout", speed: 800 }, dropShadow: { enabled: true, top: 3, left: 0, blur: 6, color: "#7c3aed", opacity: 0.15 } },
                    colors: ["#7c3aed"],
                    dataLabels: { enabled: false },
                    stroke: { curve: "smooth", width: 3 },
                    fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.02, stops: [0, 90, 100] } },
                    labels: allXValues,
                    xaxis: { type: "datetime", axisBorder: { show: false }, axisTicks: { show: false }, labels: { style: { colors: "#94a3b8", fontSize: "11px", fontWeight: 500 } } },
                    yaxis: { labels: { style: { colors: "#94a3b8", fontSize: "11px", fontWeight: 500 }, formatter: (val) => `${val}%` }, min: 0 },
                    grid: { borderColor: "#f1f5f9", strokeDashArray: 4 },
                    markers: { size: 4, colors: ["#fff"], strokeColors: "#7c3aed", strokeWidth: 2, hover: { size: 7, sizeOffset: 3 } },
                    tooltip: { theme: "light", style: { fontSize: "13px" }, x: { format: "MMM dd, yyyy" }, y: { formatter: (val) => `${val}%` } },
                  }}
                />
              </Stack>
            )}
            {dailyGrowth && (
              <Stack direction="column" spacing={2} className="box" sx={{ flex: 1 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box className="gradient-circle" sx={{ padding: "8px", alignItems: "center", justifyItems: "center" }}>
                    <IconTrendingUp size="20" className="icon" />
                  </Box>
                  <Stack direction="column">
                    <Typography variant="h4" color={"var(--main-text-color)"}>Daily Growth Rate</Typography>
                    <Typography variant="bodysm" color="var(--body-text-color-600)">Day-over-day % change</Typography>
                  </Stack>
                </Stack>
                <ReactApexChart
                  type="bar"
                  height={300}
                  series={[
                    { name: "Views Growth", data: dailyGrowth.viewsGrowth },
                    { name: "Engagement Growth", data: dailyGrowth.engGrowth },
                  ]}
                  options={{
                    chart: { type: "bar", zoom: { enabled: false }, toolbar: { show: false }, fontFamily: "inherit", animations: { enabled: true, easing: "easeinout", speed: 600 } },
                    colors: ["#7c3aed", "#f59e0b"],
                    plotOptions: { bar: { borderRadius: 4, columnWidth: "60%", borderRadiusApplication: "end" } },
                    dataLabels: { enabled: false },
                    labels: dailyGrowth.dates,
                    xaxis: { type: "datetime", axisBorder: { show: false }, axisTicks: { show: false }, labels: { style: { colors: "#94a3b8", fontSize: "11px", fontWeight: 500 } } },
                    yaxis: { labels: { style: { colors: "#94a3b8", fontSize: "11px", fontWeight: 500 }, formatter: (val) => `${val}%` } },
                    grid: { borderColor: "#f1f5f9", strokeDashArray: 4 },
                    tooltip: { shared: true, intersect: false, theme: "light", style: { fontSize: "13px" }, x: { format: "MMM dd, yyyy" }, y: { formatter: (val) => `${val}%` } },
                    legend: { position: "top", horizontalAlign: "left", fontSize: "12px", fontWeight: 600, markers: { radius: 12 }, itemMargin: { horizontal: 8 } },
                  }}
                />
              </Stack>
            )}
          </Stack>

          {/* Creator Cost Efficiency */}
          {creatorCostComparison && (
            <Stack direction="row" spacing={2}>
              <Stack direction="column" spacing={2} className="box" sx={{ flex: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Box className="gradient-circle" sx={{ padding: "8px", alignItems: "center", justifyItems: "center" }}>
                      <IconCoinMonero size="20" className="icon" />
                    </Box>
                    <Stack direction="column">
                      <Typography variant="h4" color={"var(--main-text-color)"}>Creator Cost Efficiency</Typography>
                      <Typography variant="bodysm" color="var(--body-text-color-600)">CPV & CPE by creator</Typography>
                    </Stack>
                  </Stack>
                  <ReactApexChart
                    type="bar"
                    height={300}
                    series={[
                      { name: "CPV ($)", data: creatorCostComparison.cpv },
                      { name: "CPE ($)", data: creatorCostComparison.cpe },
                    ]}
                    options={{
                      chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit", animations: { enabled: true, easing: "easeinout", speed: 600 } },
                      colors: ["#3b82f6", "#10b981"],
                      plotOptions: { bar: { horizontal: true, barHeight: "55%", borderRadius: 6, borderRadiusApplication: "end" } },
                      dataLabels: { enabled: true, formatter: (val) => `$${val.toFixed(2)}`, style: { fontSize: "11px", fontWeight: 600, colors: ["#64748b"] }, offsetX: 5 },
                      xaxis: { categories: creatorCostComparison.names, axisBorder: { show: false }, axisTicks: { show: false }, labels: { style: { colors: "#94a3b8", fontSize: "11px" }, formatter: (val) => `$${val}` } },
                      yaxis: { labels: { style: { fontSize: "13px", fontWeight: 600, colors: "#334155" } } },
                      grid: { borderColor: "#f1f5f9", strokeDashArray: 4, xaxis: { lines: { show: true } }, yaxis: { lines: { show: false } } },
                      tooltip: { shared: true, intersect: false, theme: "light", style: { fontSize: "13px" }, y: { formatter: (val) => `$${val.toFixed(2)}` } },
                      legend: { position: "top", horizontalAlign: "left", fontSize: "12px", fontWeight: 600, markers: { radius: 12 }, itemMargin: { horizontal: 8 } },
                    }}
                  />
                </Stack>
            </Stack>
          )}

          {/* CPV & CPE Over Time */}
          {campaignMetrics.budgetEngaged > 0 && allXValues.length > 0 && (
            <Stack direction="row" spacing={2}>
              <Stack direction="column" spacing={2} className="box" sx={{ flex: 1 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box className="gradient-circle" sx={{ padding: "8px", alignItems: "center", justifyItems: "center" }}>
                    <IconCoinMonero size="20" className="icon" />
                  </Box>
                  <Stack direction="column">
                    <Typography variant="h4" color={"var(--main-text-color)"}>CPV Over Time</Typography>
                    <Typography variant="bodysm" color="var(--body-text-color-600)">Cost per view as campaign progresses</Typography>
                  </Stack>
                </Stack>
                <ReactApexChart
                  type="area"
                  height={300}
                  series={[{ name: "CPV", data: campaignCostOverTime.cpv }]}
                  options={{
                    chart: { type: "area", zoom: { enabled: false }, toolbar: { show: false }, fontFamily: "inherit", animations: { enabled: true, easing: "easeinout", speed: 800 }, dropShadow: { enabled: true, top: 3, left: 0, blur: 6, color: "#3b82f6", opacity: 0.15 } },
                    colors: ["#3b82f6"],
                    dataLabels: { enabled: false },
                    stroke: { curve: "smooth", width: 2.5 },
                    fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.02, stops: [0, 90, 100] } },
                    labels: allXValues,
                    xaxis: { type: "datetime", axisBorder: { show: false }, axisTicks: { show: false }, labels: { style: { colors: "#94a3b8", fontSize: "11px", fontWeight: 500 } } },
                    yaxis: { labels: { style: { colors: "#94a3b8", fontSize: "11px", fontWeight: 500 }, formatter: (val) => `$${val.toFixed(2)}` } },
                    grid: { borderColor: "#f1f5f9", strokeDashArray: 4 },
                    markers: { size: 0, hover: { size: 5, sizeOffset: 3 } },
                    tooltip: { theme: "light", style: { fontSize: "13px" }, x: { format: "MMM dd, yyyy" }, y: { formatter: (val) => `$${val.toFixed(2)}` } },
                  }}
                />
              </Stack>
              <Stack direction="column" spacing={2} className="box" sx={{ flex: 1 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box className="gradient-circle" sx={{ padding: "8px", alignItems: "center", justifyItems: "center" }}>
                    <IconCoinMonero size="20" className="icon" />
                  </Box>
                  <Stack direction="column">
                    <Typography variant="h4" color={"var(--main-text-color)"}>CPE Over Time</Typography>
                    <Typography variant="bodysm" color="var(--body-text-color-600)">Cost per engagement as campaign progresses</Typography>
                  </Stack>
                </Stack>
                <ReactApexChart
                  type="area"
                  height={300}
                  series={[{ name: "CPE", data: campaignCostOverTime.cpe }]}
                  options={{
                    chart: { type: "area", zoom: { enabled: false }, toolbar: { show: false }, fontFamily: "inherit", animations: { enabled: true, easing: "easeinout", speed: 800 }, dropShadow: { enabled: true, top: 3, left: 0, blur: 6, color: "#10b981", opacity: 0.15 } },
                    colors: ["#10b981"],
                    dataLabels: { enabled: false },
                    stroke: { curve: "smooth", width: 2.5 },
                    fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.02, stops: [0, 90, 100] } },
                    labels: allXValues,
                    xaxis: { type: "datetime", axisBorder: { show: false }, axisTicks: { show: false }, labels: { style: { colors: "#94a3b8", fontSize: "11px", fontWeight: 500 } } },
                    yaxis: { labels: { style: { colors: "#94a3b8", fontSize: "11px", fontWeight: 500 }, formatter: (val) => `$${val.toFixed(2)}` } },
                    grid: { borderColor: "#f1f5f9", strokeDashArray: 4 },
                    markers: { size: 0, hover: { size: 5, sizeOffset: 3 } },
                    tooltip: { theme: "light", style: { fontSize: "13px" }, x: { format: "MMM dd, yyyy" }, y: { formatter: (val) => `$${val.toFixed(2)}` } },
                  }}
                />
              </Stack>
            </Stack>
          )}

          {/* Creator Comparison */}
          {creatorComparisonData.names.length > 0 && (
            <Stack direction="column" spacing={2} className="box">
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box className="gradient-circle" sx={{ padding: "8px", alignItems: "center", justifyItems: "center" }}>
                  <IconChartBar size="20" className="icon" />
                </Box>
                <Stack direction="column">
                  <Typography variant="h4" color={"var(--main-text-color)"}>Creator Comparison</Typography>
                  <Typography variant="bodysm" color="var(--body-text-color-600)">Total views and engagement by creator</Typography>
                </Stack>
              </Stack>
              <ReactApexChart
                type="bar"
                height={Math.max(300, creatorComparisonData.names.length * 70)}
                series={[
                  { name: "Views", data: creatorComparisonData.views },
                  { name: "Engagement", data: creatorComparisonData.engagement },
                ]}
                options={{
                  chart: { type: "bar", toolbar: { show: true, tools: { download: true, selection: false, zoom: false, zoomin: false, zoomout: false, pan: false, reset: false } }, fontFamily: "inherit", animations: { enabled: true, easing: "easeinout", speed: 600 } },
                  colors: ["#7c3aed", "#f59e0b"],
                  plotOptions: { bar: { horizontal: true, barHeight: "55%", borderRadius: 6, borderRadiusApplication: "end" } },
                  dataLabels: { enabled: true, formatter: (val) => formatStat(val), style: { fontSize: "11px", fontWeight: 600, colors: ["#64748b"] }, offsetX: 30 },
                  xaxis: { categories: creatorComparisonData.names, axisBorder: { show: false }, axisTicks: { show: false }, labels: { style: { colors: "#94a3b8", fontSize: "12px", fontWeight: 500 }, formatter: (val) => formatStat(val) } },
                  yaxis: { labels: { style: { fontSize: "13px", fontWeight: 600, colors: "#334155" } } },
                  grid: { borderColor: "#f1f5f9", strokeDashArray: 4, xaxis: { lines: { show: true } }, yaxis: { lines: { show: false } } },
                  tooltip: { shared: true, intersect: false, theme: "light", style: { fontSize: "13px" }, y: { formatter: (val) => val?.toLocaleString() } },
                  legend: { position: "top", horizontalAlign: "left", fontSize: "13px", fontWeight: 600, markers: { radius: 12 }, itemMargin: { horizontal: 12 } },
                }}
              />
            </Stack>
          )}
        </Stack>

      )}
      {tabValue === "details" && (
        <Stack direction="column" spacing={2} sx={{ padding: "3%" }}>
          <Typography variant="h3" color={"var(--main-text-color)"}>
            Details
          </Typography>
          <Stack className="box" direction="column" spacing={2}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  className="gradient-circle"
                  sx={{
                    padding: "8px",
                    alignItems: "center",
                    justifyItems: "center",
                  }}
                >
                  <IconFileDescription size="20" className="icon" />
                </Box>
                <Typography variant="h4" color={"var(--main-text-color)"}>
                  Description
                </Typography>
              </Stack>
            </Stack>
            <Typography variant="bodysm" color="var(--body-text-color-600)">
              {campaign.description}
            </Typography>
            <Divider />
            <Stack direction="row" width="100%" spacing={2}>
              <Stack direction="row" spacing={2} width="25%">
                <Box
                  className="gradient-circle-light"
                  sx={{
                    padding: "9px",
                    alignItems: "center",
                    justifyItems: "center",
                  }}
                >
                  <IconApps
                    size="25"
                    className="icon-colored"
                    color={theme.palette.secondary.main}
                  />
                </Box>
                <Stack direction="column" spacing={0}>
                  <Typography
                    variant="bodysm"
                    color="var(--body-text-color-600)"
                  >
                    Platforms
                  </Typography>
                  <Typography variant="bodym" color="var(--main-text-color)">
                    {Array.isArray(campaign.platforms) 
                      ? campaign.platforms.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(", ") 
                      : campaign.platforms 
                        ? campaign.platforms.charAt(0).toUpperCase() + campaign.platforms.slice(1)
                        : "-"}
                  </Typography>
                </Stack>
              </Stack>
              <Stack direction="row" width="25%" spacing={2}>
                <Box
                  className="gradient-circle-light"
                  sx={{
                    padding: "9px",
                    alignItems: "center",
                    justifyItems: "center",
                  }}
                >
                  <IconUsersGroup
                    size="25"
                    className="icon-colored"
                    color={theme.palette.secondary.main}
                  />
                </Box>
                <Stack direction="column" spacing={0}>
                  <Typography
                    variant="bodysm"
                    color="var(--body-text-color-600)"
                  >
                    Creator Levels
                  </Typography>
                  <Typography variant="bodym" color="var(--main-text-color)">
                    {Array.isArray(campaign.celebrity) 
                      ? campaign.celebrity.map(c => c.charAt(0).toUpperCase() + c.slice(1)).join(", ") 
                      : campaign.celebrity 
                        ? campaign.celebrity.charAt(0).toUpperCase() + campaign.celebrity.slice(1)
                        : "-"}
                  </Typography>
                </Stack>
              </Stack>
              <Stack direction="row" width="25%" spacing={2}>
                <Box
                  className="gradient-circle-light"
                  sx={{
                    padding: "9px",
                    alignItems: "center",
                    justifyItems: "center",
                  }}
                >
                  <IconBuilding
                    size="25"
                    className="icon-colored"
                    color={theme.palette.secondary.main}
                  />
                </Box>
                <Stack direction="column" spacing={0}>
                  <Typography
                    variant="bodysm"
                    color="var(--body-text-color-600)"
                  >
                    Industry
                  </Typography>
                  <Typography variant="bodym" color="var(--main-text-color)">
                    {Array.isArray(campaign.industries) 
                      ? campaign.industries.join(", ") 
                      : Array.isArray(campaign.industry)
                      ? campaign.industry.join(", ")
                      : campaign.industries || campaign.industry || "-"}
                  </Typography>
                </Stack>
              </Stack>
              <Stack direction="row" width="25%" spacing={2}>
                <Box
                  className="gradient-circle-light"
                  sx={{
                    padding: "9px",
                    alignItems: "center",
                    justifyItems: "center",
                  }}
                >
                  <IconMapPin
                    size="25"
                    className="icon-colored"
                    color={theme.palette.secondary.main}
                  />
                </Box>
                <Stack direction="column" spacing={0}>
                  <Typography
                    variant="bodysm"
                    color="var(--body-text-color-600)"
                  >
                    Region
                  </Typography>
                  <Typography variant="bodym" color="var(--main-text-color)">
                    {Array.isArray(campaign.region) 
                      ? campaign.region.join(", ") 
                      : campaign.region || "-"}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
          <Stack className="box" direction="column" spacing={2}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
            >
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                width="100%"
              >
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box
                    className="gradient-circle"
                    sx={{
                      padding: "8px",
                      alignItems: "center",
                      justifyItems: "center",
                    }}
                  >
                    <IconListCheck size="20" className="icon" />
                  </Box>
                  <Typography variant="h4" color={"var(--main-text-color)"}>
                    Deliverables
                  </Typography>
                </Stack>
                {/* Add progress bar once deliverables are changed to a list format with specific fields */}
                {/* <Stack sx={{ width: '15%'}}>
                                    <LinearProgress variant="determinate" value="50" color="success" sx={{borderRadius: 5, height: "6px"}}/>
                                </Stack>
                                <Typography variant="bodyxs" color="var(--body-text-color-600)">1/3 completed</Typography> */}
              </Stack>
              {/* <Button className="outlined-button" variant="outlined" startIcon={<IconEdit className="icon-colored" size="18" color={theme.palette.primary.main}/>}>Edit Deliverables</Button> */}
            </Stack>
            <Divider />
            <Typography variant="bodysm" color="var(--body-text-color-600)" style={{whiteSpace: "pre-wrap"}}>
              {Array.isArray(campaign.deliverables) ? campaign.deliverables.join("\n") : campaign.deliverables}
            </Typography>
            {/* Create table once deliverables are changed to a list format with specific fields */}
            {/* <TableContainer>
                            <Table sx={{ minWidth: 650 }}>
                                <TableBody>
                                    <TableRow>
                                        <TableCell align="right"></TableCell>
                                        <TableCell align="right"></TableCell>
                                        <TableCell align="right"></TableCell>
                                        <TableCell align="right"></TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer> */}
          </Stack>
          <Stack direction="row" spacing={2}>
            <Stack className="box" direction="column" spacing={2} width="50%">
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  className="gradient-circle"
                  sx={{
                    padding: "8px",
                    alignItems: "center",
                    justifyItems: "center",
                  }}
                >
                  <IconPresentation size="20" className="icon" />
                </Box>
                <Typography variant="h4" color={"var(--main-text-color)"}>
                  Talking Points
                </Typography>
              </Stack>
              <Divider />
              {campaign.talking_points &&
                campaign.talking_points
                  .split("\n")
                  .filter((point) => point.trim().length > 0)
                  .map((point, index) => (
                  <Stack key={index} direction="row" gap={3}>
                    <Box sx={{ width: "30px" }}>
                      <IconMicrophone2
                        className="icon-colored"
                        size="25"
                        color={theme.palette.secondary.light}
                      />
                    </Box>
                    <Typography
                      variant="bodysm"
                      color="var(--body-text-color-600)"
                    >
                      {point.replace(/^- /, "")}
                    </Typography>
                  </Stack>
                ))}
            </Stack>
            <Stack className="box" direction="column" spacing={2} width="50%">
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  className="gradient-circle"
                  sx={{
                    padding: "8px",
                    alignItems: "center",
                    justifyItems: "center",
                  }}
                >
                  <IconInfoCircle size="28" className="icon" />
                </Box>
                <Typography variant="h4" color={"var(--main-text-color)"}>
                  Resources
                </Typography>
              </Stack>
              <Divider />
              <Stack direction="column" spacing={2}>
                <Link href="/campaigns/faq" target="_blank" style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: "5px" }}>

                  <Typography variant="bodysm" color="var(--body-text-color-600)">
                    Tutorials & FAQ
                  </Typography>
                  <IconLink size="20" className="icon-colored"  color="var(--main-text-color)"/>
                </Link>
<Stack direction="row" alignItems="center" spacing={2} style={{cursor: "pointer"}} onClick={() => setGettingStartedOpen(true)}>
<Typography variant="bodysm" color="var(--body-text-color-600)">
You're a rockstar! Congrats on launching your campaign. Here are some tips to get your started

                  </Typography>
                  <IconInfoCircle size="20" className="icon-colored"  color="var(--main-text-color)"/>
                  </Stack>
              </Stack>
              {/* Add links to resources here */}
            </Stack>
          </Stack>
        </Stack>
      )}
      {tabValue === "outreach" && (
        <Stack direction="column" spacing={2} sx={{ padding: "3%" }}>
          <InviteFromListDialog open={addNewListOpen} onClose={() => setAddNewListOpen(false)} campaignId={campaignId}/>
          <Typography variant="h3" color={"var(--main-text-color)"}>
            Outreach
          </Typography>
          <Stack direction="column" spacing={2} className="box">
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{ width: "100%", flexWrap: "wrap", gap: 2 }}
            >
              <Stack direction="row" alignItems="center" spacing={2} flexShrink={0}>
                <Box
                  className="gradient-circle"
                  sx={{
                    padding: "8px",
                    alignItems: "center",
                    justifyItems: "center",
                  }}
                >
                  <IconUsers size="20" className="icon" />
                </Box>
                <Typography variant="h4" color={"var(--main-text-color)"}>
                  Invite Creators
                </Typography>
              </Stack>
              
            </Stack>
            <Stack direction="row" spacing={2}>
            <TextField
                placeholder="Search creators by name or handle..."
                size="small"
                variant="outlined"
                value={outreachNameSearch}
                onChange={(e) => setOutreachNameSearch(e.target.value)}
                sx={{
                  flex: 1,
                  minWidth: 280,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    backgroundColor: "#fff",
                    "& fieldset": { borderColor: "var(--neutral-200)" },
                    "&:hover fieldset": { borderColor: "var(--secondary-300)" },
                  },
                }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <IconSearch size={18} color="var(--body-text-color-500)" />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <Button
                startIcon={<IconUserPlus className="icon" size="18" style={{ color: "black" }} />}
                onClick={() => setAddNewListOpen(true)}
                variant="outlined"
                className="outlined-button"
                color="info"
                sx={{ flexShrink: 0 }}
              >
                Invite From My List
              </Button>
            </Stack>
            <NewOutreachTable
              campaignId={campaignId}
              outreach={outreach}
              setOutreach={setOutreach}
              outreachLoading={outreachLoading}
              onRefreshOutreach={fetchOutreach}
              nameSearch={outreachNameSearch}
              setNameSearch={setOutreachNameSearch}
            />
          </Stack>
          <CreatorCarousel
            creators={recommendations}
            title="Recommended Creators"
          />
        </Stack>
      )}
      {tabValue === "workstream" && <WorkstreamTab matches={matches} campaign={campaign} />}
      {tabValue === "applicants" && <ApplicantTab matches={matches} applications={applications} />}
      {tabValue === "live" && <LiveContent matches={matches} />}
    </Box>
  );
}
